import {
  PackageProtectionPricingStrategy,
  type PricingRuleSet,
} from "@redotech/redo-return-coverage-common/coverage-types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DEFAULT_PRICING_PERCENTAGE,
  PricingRuleSetSchema,
  pricingRuleSetsFromSettings,
} from "./pricing-rule-set";

export const DEFAULT_MIN_PRICE = 1;

/** The default 2% bracket applied to the $5000 default cart cap. */
export const DEFAULT_MAX_PRICE = 100;

/** Money is numbers here; the merchant app adapts its text inputs either way. */
export const PackageProtectionPricingSchema = z.object({
  packageProtectionPlusEnabled: z.boolean(),
  pricingStrategy: createEnum(PackageProtectionPricingStrategy).nullable(),
  minPrice: z.number(),
  maxPrice: z.number(),
  percentage: z.number(),
  minCoverageEligibleCartTotal: z.number().nullable(),
  maxCoverageEligibleCartTotal: z.number().nullable(),
  fixedSubscriptionPrice: z.number().nullable(),
  pricingRuleSet: z.array(PricingRuleSetSchema),
});

export type PackageProtectionPricing = z.infer<
  typeof PackageProtectionPricingSchema
>;

/** What the read needs, tolerant of the nulls the raw team document carries. */
export interface PackageProtectionPricingSource {
  packageProtectionPlusEnabled?: boolean | null;
  pricingStrategy?: PackageProtectionPricingStrategy | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  percentage?: number | null;
  minCoverageEligibleCartTotal?: number | null;
  maxCoverageEligibleCartTotal?: number | null;
  fixedSubscriptionPrice?: number | null;
  pricingRuleSet?: PricingRuleSet[] | null;
}

export function packageProtectionPricingFromSettings({
  packageProtection,
  countriesPackageProtection,
}: {
  packageProtection: PackageProtectionPricingSource | null | undefined;
  countriesPackageProtection: string[];
}): PackageProtectionPricing {
  return {
    packageProtectionPlusEnabled:
      packageProtection?.packageProtectionPlusEnabled ?? false,
    pricingStrategy:
      packageProtection?.pricingStrategy ??
      PackageProtectionPricingStrategy.CART_VALUE,
    minPrice: packageProtection?.minPrice ?? DEFAULT_MIN_PRICE,
    maxPrice: packageProtection?.maxPrice ?? DEFAULT_MAX_PRICE,
    percentage: packageProtection?.percentage ?? DEFAULT_PRICING_PERCENTAGE,
    minCoverageEligibleCartTotal:
      packageProtection?.minCoverageEligibleCartTotal ?? null,
    maxCoverageEligibleCartTotal:
      packageProtection?.maxCoverageEligibleCartTotal ?? null,
    fixedSubscriptionPrice: packageProtection?.fixedSubscriptionPrice ?? null,
    pricingRuleSet: pricingRuleSetsFromSettings({
      pricingRuleSet: packageProtection?.pricingRuleSet,
      percentage: packageProtection?.percentage,
      countries: countriesPackageProtection,
    }),
  };
}
